import { AbstractAgent, HttpAgent, MiddlewareFunction, RunAgentInput } from "@ag-ui/client";
import { from, switchMap } from "rxjs";
import { BubbleProps } from "@ant-design/x";
import { useCallback, useRef, useSyncExternalStore } from "react";
import { MessageStatus } from "@ant-design/x/es/bubble/interface";
import { v4 as uuidv4 } from "uuid";

const isProdEnvironment = window.location.hostname.includes("hub.enit.io");
export const AGENT_URL = isProdEnvironment
  ? "https://gateway.ai.proalpha.dev/api/runtime/enit-copilot-prod/agui"
  : "https://gateway.ai.proalpha.dev/api/runtime/enit-copilot-dev/agui";
export const MCP_SERVER_ID = isProdEnvironment ? "enit-mcp-prod-aifenittest" : "mcp-test";

export type RunCallbacks = {
  onError: (error: Error) => void;
};

export function makeAuthMiddleware(
  httpAgent: HttpAgent,
  getAccessTokenSilently: () => Promise<string>
): MiddlewareFunction {
  return function authMiddleware(input, next) {
    return from(getAccessTokenSilently()).pipe(
      switchMap((token) => {
        httpAgent.headers = {
          ...httpAgent.headers,
          Authorization: `Bearer ${token}`,
        };

        return next.run(input);
      })
    );
  };
}

export function makeOrgaMiddleware(orgaId: string): MiddlewareFunction {
  return function orgaMiddleware(input, next) {
    const inputWithOrgaId: RunAgentInput = {
      ...input,
      forwardedProps: {
        ...input.forwardedProps,
        mcp_headers: {
          [MCP_SERVER_ID]: { "X-Enit-Orga-Id": orgaId },
        },
      },
    };

    return next.run(inputWithOrgaId);
  };
}

export function createAgent(
  getAccessTokenSilently: () => Promise<string>,
  orgaId: string
): AbstractAgent {
  const agent = new HttpAgent({ url: AGENT_URL, threadId: uuidv4() });
  const authMiddleware = makeAuthMiddleware(agent, getAccessTokenSilently);
  const orgaMiddleware = makeOrgaMiddleware(orgaId);
  agent.use(authMiddleware, orgaMiddleware);
  return agent;
}

export async function runAgent(
  agent: AbstractAgent,
  prompt: string,
  callbacks: RunCallbacks
): Promise<void> {
  const messagesBeforeRun = [...agent.messages];

  agent.addMessage({
    id: crypto.randomUUID(),
    role: "user",
    content: prompt,
  });

  try {
    await agent.runAgent(
      {},
      {
        onRunErrorEvent({ event }) {
          throw new Error(event.message);
        },
      }
    );
  } catch (error) {
    agent.setMessages(messagesBeforeRun);
    callbacks.onError(error instanceof Error ? error : new Error(String(error)));
  }
}

type ChatState = {
  messages: Array<ChatMessage>;
  isRunning: boolean;
  isCallingTool: boolean;
  isStreaming: boolean;
};

function toChatMessages(agent: AbstractAgent): Array<ChatMessage> {
  return agent.messages.flatMap((message): Array<ChatMessage> => {
    if (message.role === "user") {
      return [
        {
          key: message.id,
          role: "user",
          status: MessageStatus.local,
          content: typeof message.content === "string" ? message.content : "",
        },
      ];
    } else if (message.role === "assistant" && message.content) {
      return [
        {
          key: message.id,
          role: "ai",
          status: MessageStatus.success,
          content: message.content,
        },
      ];
    }
    return [];
  });
}

export type ChatMessage = BubbleProps & {
  key: string;
  role: string;
  status: MessageStatus;
};

export function useCopilotChat(agent: AbstractAgent): ChatState {
  const snapshotRef = useRef<{
    agent: AbstractAgent | null;
    revision: number;
    snapshot: ChatState;
  }>({
    agent: null,
    revision: -1,
    snapshot: {
      messages: [],
      isRunning: false,
      isStreaming: false,
      isCallingTool: false,
    },
  });
  const latestRevisionRef = useRef(0);

  const subscribe = useCallback(
    (listener: () => void) => {
      function triggerSnapshotUpdate() {
        latestRevisionRef.current += 1;
        listener();
      }

      const { unsubscribe } = agent.subscribe({
        onMessagesChanged: triggerSnapshotUpdate,
        onStateChanged: triggerSnapshotUpdate,
        onRunFinalized: () => {
          snapshotRef.current.snapshot.isCallingTool = false;
          snapshotRef.current.snapshot.isStreaming = false;
          triggerSnapshotUpdate();
        },
        onToolCallStartEvent: () => {
          snapshotRef.current.snapshot.isCallingTool = true;
          triggerSnapshotUpdate();
        },
        onToolCallResultEvent: () => {
          snapshotRef.current.snapshot.isCallingTool = false;
          triggerSnapshotUpdate();
        },
        onTextMessageStartEvent: () => {
          snapshotRef.current.snapshot.isStreaming = true;
          triggerSnapshotUpdate();
        },
        onTextMessageEndEvent: () => {
          snapshotRef.current.snapshot.isStreaming = false;
          triggerSnapshotUpdate();
        },
      });

      return unsubscribe;
    },
    [agent]
  );

  function getSnapshot(): ChatState {
    const agentChanged = snapshotRef.current.agent !== agent;
    const revisionChanged = snapshotRef.current.revision !== latestRevisionRef.current;

    if (agentChanged || revisionChanged) {
      snapshotRef.current = {
        agent,
        revision: latestRevisionRef.current,
        snapshot: {
          messages: toChatMessages(agent),
          isRunning: agent.isRunning,
          isStreaming: agentChanged ? false : snapshotRef.current.snapshot.isStreaming,
          isCallingTool: agentChanged ? false : snapshotRef.current.snapshot.isCallingTool,
        },
      };
    }
    return snapshotRef.current.snapshot;
  }

  return useSyncExternalStore(subscribe, getSnapshot);
}
